<template>
    <div :class="namespaceClass()">
        <mds-loader v-if="tableDataLoading"></mds-loader>
        <div :class="namespaceClass('table-container')">
            <div v-if="paginationDataLoading" :class="namespaceClass('pagination-loader')">
                <mds-loader size="medium"></mds-loader>
            </div>
            <mds-data-table
                v-if="!tableDataLoading && showRows.length > 0"
                :class="namespaceClass('data-table-container')"
                caption="List Hypotheticals illustration"
                size="medium"
                multiselection
                row-hover
                pagination="below"
                :pagination-config="paginationOptions"
                :header-configs="this.getComponentConfig().settings.columns"
                :row-data="showRows"
                @mds-data-table-sort-change="onSort"
                @mds-data-table-page-change="onPaginateTable"
                @mds-data-table-row-selection-change="onSelectRow"
                @mds-data-table-header-select-all="onSelectAll"
            >
                <template v-slot:body-cell="cellData">
                    <template v-if="cellData.colIndex === 0">
                        <a @click="onClickRow(cellData.rowData)" :class="namespaceClass('cell-name-link')">{{
                            cellData.rowData[cellData.headerConfig.fieldName]
                        }}</a>
                        <mds-tag class="firm-saved-tag" v-if="cellData.rowData.rightsId === 3">
                            {{ getUILabel("ARCHIVE_REPORTS_TAG_FIRM_SAVED") }}
                        </mds-tag>
                    </template>
                </template>
            </mds-data-table>
            <div v-if="isEmptyDataTable" :class="namespaceClass('empty-datatable')">
                <span :class="namespaceClass('empty-state-title')">{{ getUILabel("ARCHIVE_HYPOS_EMPTY_HYPO_TITLE") }}</span>
                <span :class="namespaceClass('this-is-a-supporting')">{{ getUILabel("ARCHIVE_HYPOS_EMPTY_HYPO_DESCRIPTION") }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import { MdsDataTable } from "@mds/data-table";
import MdsLoader from "@mds/loader";
import { dateFormat, stringFormat, isEqualTimePeriod, isValidCommonEndDate } from "@/util.js";
import { MdsTag } from "@mds/tag";
import * as types from "@/store/mutation-types";

export default {
    name: "hypotheticals-dashboard",
    mixins: [HypoVueBaseMixin, MwcSharedMixin],
    components: {
        MdsDataTable,
        MdsLoader,
        MdsTag
    },
    props: {
        filter: {
            type: String,
            default: "all"
        }
    },
    data() {
        return {
            pageLoading: false,
            tableDataLoading: true,
            paginationDataLoading: false,
            buttonDisabled: true,
            pageIndex: 1,
            pageSize: 50,
            keyword: "",
            sortBy: "modifiedTime",
            descending: true,
            onlyFirmfile: false,
            firstItem: 1,
            currentPage: 1,
            currentShow: 50,
            lastItem: 0,
            paginationOptions: {
                totalItems: 100,
                showItemsInfo: true,
                showItemsSelect: false,
                pageSize: 50,
                page: 1
            },
            hypoList: [],
            showRows: [],
            checkedRows: [],
            // mwc settings
            defaultConfig: {
                settings: {
                    columns: [
                        {
                            fieldName: "hypoName",
                            align: "left",
                            width: "50%",
                            sortable: true,
                            transform: text => stringFormat(text)
                        },
                        {
                            fieldName: "modifiedTime",
                            align: "right",
                            sortable: true,
                            sorted: -1,
                            transform: dateText => dateFormat(dateText)
                        }
                    ]
                }
            },
            labels: this.$store.state.labels.labels,
            format: {
                date: {
                    dataType: "date",
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "numeric",
                    minute: "numeric"
                }
            }
        };
    },
    computed: {
        isEmptyDataTable() {
            return !this.paginationDataLoading && !this.tableDataLoading && this.showRows.length <= 0;
        }
    },
    mounted() {
        this.settings.columns[0].text = this.getUILabel("ARCHIVE_HYPOS_COLUMN_HYPO_NAME");
        this.settings.columns[1].text = this.getUILabel("ARCHIVE_HYPOS_COLUMN_MODIFIED_TIME");
        this.getHypofileCount();
        this.initTable();
    },
    watch: {
        checkedRows(newValue) {
            // Remove this constant will cause test cases failures
            this.buttonDisabled = newValue.length === 0;
            this.$emit("delete-dissabled", this.buttonDisabled);
            this.$emit("duplicate-dissabled", this.buttonDisabled);
        },
        filter(newVal) {
            if (newVal === "all") {
                this.onlyFirmfile = false;
            } else {
                this.onlyFirmfile = true;
            }
            this.initTable();
        }
    },
    methods: {
        initTable() {
            this.checkedRows = [];
            this.getHypotheticalList().then(data => {
                this.tableDataLoading = false;
                this.showRows = data;
                this.sortData = this.showRows;
                this.lastItem = this.showRows.length;
            });
        },
        getHypofileCount() {
            this.$fetch({
                url: "/api/v1/Hypo",
                method: "GET"
            }).then(respData => {
                this.paginationOptions.totalItems = respData.totalCount;
                this.hypoList = respData.entities;
                this.$store.dispatch("notifications/setHypoCount", respData.totalCount);
                this.$store.dispatch("notifications/setHypoList", respData.entities);
            });
        },
        getHypotheticalList() {
            const params = new URLSearchParams({
                pageIndex: this.pageIndex,
                pageSize: this.pageSize,
                sortBy: this.sortBy,
                descending: this.descending,
                keyword: this.keyword,
                onlyFirmfile: this.onlyFirmfile
            });
            return this.$fetch({
                url: `/api/v1/Hypo?${params.toString()}`,
                method: "GET"
            }).then(respData => {
                this.totalCount = respData.totalCount;
                this.paginationOptions.totalItems = this.totalCount;
                this.paginationOptions.pageSize = this.pageSize;
                this.paginationOptions.page = this.pageIndex;
                this.hypoList = respData.entities.map(item => {
                    item.id = item.hypoId;
                    item.modifiedTime = this.convertUTCTimetoLocalTime(item.modifiedTime);
                    return item;
                });
                return this.hypoList;
            });
        },
        convertUTCTimetoLocalTime(timeString) {
            const d = new Date(timeString);
            const utcDate = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());
            return new Date(utcDate);
        },
        handleSearch(keyWord) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                this.keyword = keyWord;
                this.pageIndex = 1;
                this.paginationDataLoading = true;
                this.onPaginateTable({
                    firstItem: 1,
                    lastItem: 50,
                    page: 1,
                    pageSize: 50
                });
            }, 1000);
        },
        checkErrorSecurities(securities) {
            if (securities.every(item => item.errorSecurities.length === 0)) {
                return null;
            }
            const errorPortfolioIndex = securities.findIndex(item => item.errorSecurities.length > 0);
            return { index: errorPortfolioIndex };
        },
        validAssumptions(hypoAssumptions) {
            if (hypoAssumptions.every(item => item.valid === true)) {
                return null;
            }
            const errorPortfolioIndex = hypoAssumptions.findIndex(item => item.valid === false);
            return { index: errorPortfolioIndex };
        },
        validatePortfolios(portfolioList) {
            return portfolioList.findIndex(portfolio => portfolio.securities.length === 0);
        },
        validateHoldingsTimePeriod(portfolioList) {
            if (portfolioList.length > 1 && portfolioList[1].securities.length > 0) {
                return isEqualTimePeriod(portfolioList);
            }
            return true;
        },
        async checkRedirect(hypoInput, errors) {
            const { securities, assumptions } = errors;
            const { portfolioList = [], hypoType } = hypoInput;
            const invalidPortfolioIndex = this.validatePortfolios(portfolioList);
            const validateSecurities = this.checkErrorSecurities(securities);
            const validateAssumptions = this.validAssumptions(assumptions);
            if (invalidPortfolioIndex !== -1) {
                return `/workflow/AddHoldings/${invalidPortfolioIndex}`;
            } else if (validateSecurities !== null) {
                return `/workflow/AddHoldings/${validateSecurities.index}`;
            } else if (validateAssumptions !== null) {
                return `/workflow/EditAssumptions/${validateAssumptions.index}`;
            } else if (!isValidCommonEndDate(portfolioList)) {
                return `/workflow/EditAssumptions/0`;
            } else if (!this.validateHoldingsTimePeriod(portfolioList)) {
                return `/workflow/EditAssumptions/1`;
            } else {
                //consider prod server old case with none portfolio exists.
                let portfolioIndex = portfolioList.findIndex(p => p.securities.length === 0);
                if (hypoType === 1 && portfolioList.length === 1) {
                    portfolioIndex = 1;
                    await this.$store.dispatch("investments/setNewPortfolioByIndex", 1);
                }
                const redirectTo = hypoInput.hypoType === 0 ? "/workflow/AddHoldings/0" : `/workflow/AddHoldings/${portfolioIndex}`;
                return portfolioIndex >= 0 ? redirectTo : "/workflow/Summary";
            }
        },
        loadHypoFileData() {
            this.pageLoading = true;
            return this.$store
                .dispatch("getHypo", {
                    fetch: this.$fetch,
                    id: this.openedHypoId
                })
                .then(data => {
                    this.pageLoading = false;
                    return data;
                });
        },
        onPaginateTable(arg, initMount = false) {
            this.firstItem = arg.firstItem;
            this.lastItem = arg.lastItem;
            this.currentPage = arg.page;
            this.currentShow = arg.pageSize;
            this.pageIndex = arg.page;
            this.pageSize = arg.pageSize;
            this.paginationDataLoading = true;
            this.getHypotheticalList().then(data => {
                this.showRows = data;
                this.paginationDataLoading = false;
            });
        },
        onSort(arg) {
            const order = arg.sortOrder;
            const field = arg.sortField;
            this.sortBy = field;
            this.descending = order !== 1;
            this.pageIndex = 1;
            this.currentPage = 1;
            this.onPaginateTable({
                firstItem: 1,
                lastItem: 50,
                page: 1,
                pageSize: 50
            });
        },
        onSelectRow(arg) {
            if (arg.checked) {
                this.checkedRows.push(arg.id);
            } else {
                this.checkedRows = this.checkedRows.filter(x => x !== arg.id);
            }
        },
        onSelectAll(arg) {
            this.showRows = this.showRows.map(item => {
                item.selected = arg;
                return item;
            });
        },
        handleDelete() {
            if (this.checkedRows.length <= 0) {
                return null;
            }
            this.tableDataLoading = true;
            const hyposToDelete = this.hypoList.filter(item => item.selected && item.rightsId !== 3);
            this.checkedRows = hyposToDelete.map(item => item.id);
            return this.$fetch({
                url: "/api/v1/Hypo/BatchDelete",
                method: "POST",
                body: JSON.stringify(this.checkedRows)
            }).then(data => {
                const notification = {
                    size: "small",
                    variation: "success",
                    title: "",
                    persistent: false,
                    text: "Hypothetical file(s) successfully deleted."
                };
                this.$store.dispatch("notifications/showNotification", notification);
                this.tableDataLoading = false;
                this.hypoList = this.hypoList.filter(item => !item.selected);
                this.showRows = this.showRows.filter(item => !this.checkedRows.includes(item.hypoId));
                this.checkedRows = [];
                this.getHypofileCount();
                this.onPaginateTable({
                    firstItem: 1,
                    lastItem: 50,
                    page: 1,
                    pageSize: 50
                });
            });
        },
        onClickRow(arg) {
            if (arg.rightsId === 3) {
                this.$store.commit(`hypo/${types.SET_FIRM_SAVED}`, true);
            }
            this.openedHypoId = arg.id;
            this.loadHypoFileData()
                .then(data => {
                    const { hypoInput, errors } = data;
                    this.$store.dispatch("stepper/setFlow", hypoInput.hypoType);
                    this.openedHypoId = "";
                    const hasPortfolio = hypoInput.portfolioList.length > 0;
                    if (hasPortfolio) {
                        this.$store.commit("chart/SET_RELOAD_CHART_DATA", true);
                        if (this.$store.state.config.adminToolConfig.assumptionWorkflow === 1) {
                            hypoInput.portfolioList = hypoInput.portfolioList.map(item => {
                                if (!item.rebalance) {
                                    return item;
                                }
                                return { ...item, rebalance: { ...item.rebalance, rebalancingTo: 0 } };
                            });
                            this.$store.commit("investments/UPDATE_PORTFOLIOLIST", hypoInput.portfolioList);
                            this.$store.commit("investments/UPDATE_RAW_PORTFOLIOLIST", hypoInput.portfolioList);
                        }
                        this.checkRedirect(hypoInput, errors).then(redirect => {
                            this.$router.push(redirect);
                        });
                    } else {
                        this.$router.push("/workflow/AddHoldings/0");
                    }
                })
                .catch(error => {
                    this.pageLoading = false;
                    const notification = {
                        size: "medium",
                        variation: "error",
                        title: "",
                        persistent: false,
                        text: "Error opening Hypo File."
                    };
                    this.$store.dispatch("notifications/showNotification", notification);
                });
        },
        sendHypoDataForDuplicate() {
            return this.$store.dispatch("clearHypoInput").then(() => {
                return this.$store
                    .dispatch("fetchHypo", {
                        fetch: this.$fetch,
                        id: this.checkedRows[0]
                    })
                    .then(data => {
                        return data;
                    })
                    .catch(err => console.log(err));
            });
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~@mds/link";
@import "@mds/typography";
@import "~@mds/constants";
.hypotheticals-dashboard {
    &-actions {
        display: flex;
        margin-top: 27px;
        margin-bottom: 6px;
        height: 24px;
    }

    &-searchBy {
        margin-bottom: 0px;
        margin-left: $mds-space-1-x;
        width: 230px;
    }

    &-hypo-list {
        height: calc(100vh - 199px);
    }

    &-table-container {
        position: relative;
        margin-bottom: $mds-space-6-x;
    }

    &-pagination-loader {
        position: absolute;
        top: 5%;
        left: 50%;
    }

    &-cell-name-link {
        @include mds-link($underline: false);
        cursor: pointer;
    }

    &-empty-datatable {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    &-empty-state-title {
        @include mds-level-4-heading();
        color: $mds-background-color-dark-gray;
        margin-top: $mds-space-8-x;
    }
    &-this-is-a-supporting {
        @include mds-body-text-m();
        color: $mds-background-color-dark-gray;
        margin-top: $mds-space-1-x;
    }
    .firm-saved-tag {
        margin-left: $mds-space-1-x;
    }
}
</style>
